import { useMemo, useState } from "react";
import type { DailyRow, Mall } from "../types/pl";
import { bucketChart, md, mdw } from "../lib/plChart";
import { man, yen } from "../lib/plFormat";

const MALL_ORDER: Mall[] = ["shopify", "rakuten", "amazon"];
const MALL_COLOR: Record<Mall, string> = { shopify: "#5a8f4e", rakuten: "#c43d3a", amazon: "#e0922a" };
const MALL_TEXT: Record<Mall, string> = { shopify: "#4a7a40", rakuten: "#b0332f", amazon: "#a9661a" };
const MALL_LABEL: Record<Mall, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };

interface StackedBarChartProps {
  daily: DailyRow[];
  from: string;
  to: string;
  metric: "sales" | "ad_spend";
  mallFilter?: Mall | null;
  height?: number;
  /** 概要タブの売上チャートだけ貢献利益ドットを出す。 */
  showProfitDot?: boolean;
  selectedDate?: string | null;
}

/** モール別積み上げ棒＋（概要のみ）利益ドット＋ホバー情報帯。外部チャートライブラリ非依存（div積み上げ）。 */
export function StackedBarChart({
  daily,
  from,
  to,
  metric,
  mallFilter = null,
  height = 220,
  showProfitDot = false,
  selectedDate = null,
}: StackedBarChartProps) {
  const { bars, step } = useMemo(
    () => bucketChart(daily, from, to, metric, mallFilter ?? null),
    [daily, from, to, metric, mallFilter]
  );
  const [hover, setHover] = useState<number | null>(null);
  const malls = mallFilter ? [mallFilter] : MALL_ORDER;

  const max = Math.max(1, ...bars.map((b) => malls.reduce((s, m) => s + (b.values[m] ?? 0), 0)));
  const mag = Math.pow(10, Math.floor(Math.log10(max)));
  const roundedMax = mag > 0 ? (Math.ceil((max / mag) * 2) / 2) * mag : max;
  const every = Math.max(1, Math.ceil(bars.length / 12));

  let lastHas = 0;
  bars.forEach((b, i) => {
    if (b.has) lastHas = i;
  });
  const selIdx = selectedDate ? bars.findIndex((b) => b.a <= selectedDate && selectedDate <= b.b) : -1;
  const hv = hover != null ? hover : selIdx >= 0 ? selIdx : lastHas;
  const h = bars[hv];
  const total = h ? malls.reduce((s, m) => s + (h.values[m] ?? 0), 0) : 0;

  return (
    <div className="pl-chart">
      <div className="pl-chart-unit">{step === 7 ? "週次集計（期間が長いため）" : "日次"}</div>
      {h && (
        <div className="pl-chart-info">
          <span className="pl-chart-info-label">{step === 7 ? `${md(h.a)}–${md(h.b)}` : mdw(h.a)}</span>
          <span>
            {metric === "sales" ? "売上" : "広告費"} {yen(total)}
          </span>
          {malls.map((m) => (
            <span key={m} style={{ color: MALL_TEXT[m] }}>
              {MALL_LABEL[m]} {yen(h.values[m] ?? 0)}
            </span>
          ))}
          {showProfitDot && <span style={{ fontWeight: 600 }}>利益 {yen(h.profit)}</span>}
        </div>
      )}
      <div className="pl-chart-body">
        <div className="pl-chart-yaxis" style={{ height }}>
          <span>{man(roundedMax)}</span>
          <span>{man(roundedMax / 2)}</span>
          <span>0</span>
        </div>
        <div className="pl-chart-plot-wrap">
          <div className="pl-chart-plot" style={{ height }} onMouseLeave={() => setHover(null)}>
            {bars.map((b, i) => {
              const opacity = !b.has ? 0.25 : i === hv ? 1 : hover != null || selIdx >= 0 ? 0.55 : 1;
              return (
                <div key={i} className="pl-chart-col" style={{ opacity }} onMouseEnter={() => setHover(i)}>
                  {[...malls].reverse().map((m) => (
                    <div
                      key={m}
                      className="pl-chart-seg"
                      style={{ height: `${((b.values[m] ?? 0) / (roundedMax || 1)) * 100}%`, background: MALL_COLOR[m] }}
                    />
                  ))}
                  {showProfitDot && (
                    <div
                      className="pl-chart-dot"
                      style={{
                        bottom: `${Math.max(0, (b.profit / (roundedMax || 1)) * 100)}%`,
                        background: b.profit < 0 ? "#b23a2e" : "#1f2523",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="pl-chart-labels">
            {bars.map((b, i) => (
              <span key={i}>{i % every === 0 ? md(b.a) : ""}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
